@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginemodule

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.DashboardTheme
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File

@Composable
fun CreateLayout(
    modules: List<ShopModuleMeta>,
    moduleStates: Map<String, ModuleState>,
    progressMap: Map<String, Int>,
    creatingModule: Boolean,
    selectedModuleKey: String?,
    selectedModuleKeys: Set<String>,
    canDeleteSelected: Boolean,
    isEditing: Boolean,
    onSelectModuleKey: (String?) -> Unit,
    onToggleModuleSelection: (String, Boolean) -> Unit,
    onCreateModule: () -> Unit,
    onEditSelected: () -> Unit,
    onActivateSelected: () -> Unit,
    onSaveEditor: () -> Unit,
    onDeleteSelected: () -> Unit,
    onDeactivateSelected: () -> Unit,
    onInstall: (ShopModuleMeta) -> Unit,
    onActivate: (ShopModuleMeta) -> Unit,
    onDeactivate: (ShopModuleMeta) -> Unit,
    onOpenDetails: (ShopModuleMeta) -> Unit,
    editableModule: All? = null,
    onEditableModuleChange: (All) -> Unit = {},
    onLoadModuleAssets: suspend () -> List<Int> = { emptyList() },
    onUploadModuleAsset: suspend (File, String) -> Int? = { _, _ -> null },
    onDeleteModuleAsset: suspend (Int) -> Boolean = { false }
) {
    Style(cssRules = SharedWorkspaceStyle.cssRules)
    val internalTitleVisible = LocalScreenInternalTitleVisible.current

    val hasSelection = selectedModuleKeys.isNotEmpty()
    val hasSingleSelection = selectedModuleKeys.size == 1
    val dropdownOptions = modules.map {
        All(
            key = it.key,
            label = "${it.title.ifBlank { "Modul ${it.moduleId}" }} (${it.version})"
        )
    }

    Div({
        classes(SharedWorkspaceStyle.workspace)
        if (isEditing) {
            style {
                property("height", "auto")
                property("min-height", "auto")
                overflow("visible")
            }
        }
    }) {
        Div({
            classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content)
            style {
                width(100.percent)
                if (isEditing) {
                    property("min-height", "auto")
                    overflow("visible")
                }
            }
        }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    width(100.percent)
                    maxWidth(100.percent)
                    if (!isEditing) {
                        flex(1)
                        minHeight(0.px)
                    } else {
                        property("min-height", "auto")
                        overflow("visible")
                    }
                    alignItems(AlignItems.Stretch)
                }
            }) {
                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Modul Editor") }
                    }
                }

                Div({
                    classes(SharedWorkspaceStyle.actionBar)
                    style {
                        width(100.percent)
                        maxWidth(100.percent)
                        padding(0.px, 24.px)
                        boxSizing("border-box")
                        gridTemplateColumns(if (isEditing) "220px repeat(10, minmax(0, 160px))" else "220px repeat(7, minmax(0, 160px))")
                    }
                }) {
                    AppDropdown(
                        options = dropdownOptions,
                        selectedValue = selectedModuleKey,
                        onChange = {
                            val value = it.ifBlank { null }
                            onSelectModuleKey(value)
                        },
                        placeholder = "Modul wählen",
                        appearance = AppDropdownAppearance.Field,
                        attrs = { style { width(220.px); height(42.px) } }
                    )
                    LumaticaButton(label = if (creatingModule) "Erstelle..." else "Erstellen", onClick = onCreateModule, variant = LumaticaButtonVariant.Action, enabled = !creatingModule, heightPx = 42)
                    LumaticaButton(label = "Bearbeiten", onClick = onEditSelected, variant = LumaticaButtonVariant.Standard, enabled = hasSingleSelection, heightPx = 42)
                    LumaticaButton(label = "Aktivieren", onClick = onActivateSelected, variant = LumaticaButtonVariant.Standard, enabled = hasSelection && !isEditing, heightPx = 42)
                    LumaticaButton(label = "Speichern", onClick = onSaveEditor, variant = LumaticaButtonVariant.Action, enabled = isEditing, heightPx = 42)
                    LumaticaButton(label = "Löschen", onClick = onDeleteSelected, variant = LumaticaButtonVariant.Standard, enabled = canDeleteSelected && !isEditing, heightPx = 42)
                    LumaticaButton(label = "Deaktivieren", onClick = onDeactivateSelected, variant = LumaticaButtonVariant.Standard, enabled = hasSelection && !isEditing, heightPx = 42)
                }

                Div({
                    classes(SharedWorkspaceStyle.centeredDivider)
                    style {
                        width(100.percent)
                        maxWidth(100.percent)
                        marginTop(40.px)
                        marginBottom(40.px)
                    }
                })

                Div({
                    style {
                        width(100.percent)
                        if (!isEditing) {
                            flex(1)
                            minHeight(0.px)
                        } else {
                            property("min-height", "auto")
                        }
                        overflowY(if (isEditing) "visible" else "auto")
                    }
                }) {
                    if (isEditing && editableModule != null) {
                        Div({
                            style {
                                width(100.percent)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.FlexStart)
                                justifyContent(JustifyContent.Center)
                                padding(0.px, 24.px, 40.px, 24.px)
                                boxSizing("border-box")
                            }
                        }) {
                            Div({
                                style {
                                    property("width", "fit-content")
                                    maxWidth(100.percent)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.FlexStart)
                                    justifyContent(JustifyContent.Center)
                                    gap(64.px)
                                    flexWrap(FlexWrap.Nowrap)
                                }
                            }) {
                                Div({
                                    style {
                                        width(360.px)
                                        flexShrink(0)
                                        paddingTop(24.px)
                                        boxSizing("border-box")
                                    }
                                }) {
                                    EditorModuleCard(
                                        module = editableModule.toShopMeta(),
                                        state = deriveEditorPreviewState(editableModule),
                                        selected = true,
                                        onSelect = {},
                                        showStateMeta = true
                                    )
                                }
                                Div({
                                    style {
                                        width(1.px)
                                        alignSelf(AlignSelf.Stretch)
                                        minHeight(100.percent)
                                        backgroundColor(rgba(255, 255, 255, 0.14))
                                        flexShrink(0)
                                    }
                                })
                                Div({
                                    style {
                                        width(780.px)
                                        maxWidth(100.percent)
                                        flexShrink(1)
                                        display(DisplayStyle.Flex)
                                        justifyContent(JustifyContent.Center)
                                        alignItems(AlignItems.FlexStart)
                                        property("height", "auto")
                                        property("min-height", "fit-content")
                                    }
                                }) {
                                    CreateDetailLayout(
                                        module = editableModule,
                                        onChange = onEditableModuleChange,
                                        onClose = onSaveEditor,
                                        onLoadModuleAssets = onLoadModuleAssets,
                                        onUploadModuleAsset = onUploadModuleAsset,
                                        onDeleteModuleAsset = onDeleteModuleAsset
                                    )
                                }
                            }
                        }
                    } else if (modules.isEmpty()) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.Center)
                                color(Color.white)
                                fontSize(14.px)
                                padding(40.px)
                            }
                        }) { Text("Keine Module vorhanden.") }
                    } else {
                        Div({
                            style {
                                width(100.percent)
                                display(DisplayStyle.Flex)
                                flexWrap(FlexWrap.Wrap)
                                justifyContent(JustifyContent.FlexStart)
                                alignItems(AlignItems.FlexStart)
                                gap(16.px)
                                padding(0.px, 24.px, 40.px, 24.px)
                                boxSizing("border-box")
                            }
                        }) {
                            modules.forEach { module ->
                                val state = moduleStates[module.key] ?: ModuleStateVal.AVAILABLE
                                val progress = progressMap[module.key] ?: 0
                                Div({
                                    style {
                                        width(360.px)
                                        flexShrink(0)
                                    }
                                }) {
                                    EditorModuleCard(
                                        module = module,
                                        state = state,
                                        selected = module.key in selectedModuleKeys,
                                        onSelect = { multi -> onToggleModuleSelection(module.key, multi) },
                                        showStateMeta = true
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

fun deriveEditorPreviewState(module: All): ModuleState =
    if (normalizeGlobalModuleState(module.zustand) == ZustandManager.Modul.State.AKTIVIERT) {
        ModuleStateVal.INSTALLED
    } else {
        ModuleStateVal.DISABLED
    }

private fun editorStateLabel(module: ShopModuleMeta): String = when (normalizeGlobalModuleState(module.globalState)) {
    ZustandManager.Modul.State.AKTIVIERT -> ZustandManager.Modul.textFor(ZustandManager.Modul.State.AKTIVIERT)
    else -> ZustandManager.Modul.textFor(ZustandManager.Modul.State.DEAKTIVIERT)
}

private fun editorStateColor(module: ShopModuleMeta): CSSColorValue =
    Color(ZustandManager.Modul.colorHexFor(normalizeGlobalModuleState(module.globalState)))

@Composable
fun EditorModuleCard(
    module: ShopModuleMeta,
    state: ModuleState,
    selected: Boolean,
    onSelect: (Boolean) -> Unit,
    showStateMeta: Boolean = false,
    showInstalledMeta: Boolean = false
) {
    var hovered by remember(module.key, selected) { mutableStateOf(false) }
    val borderColor = when {
        selected -> UISettings.ACCENT
        state == ModuleStateVal.INSTALLED -> Color("rgba(102,187,106,0.72)")
        state == ModuleStateVal.UPDATE_AVAILABLE -> Color("rgba(143,210,255,0.72)")
        state == ModuleStateVal.PURCHASED -> Color("rgba(212,194,124,0.68)")
        else -> Color("rgba(255,255,255,0.10)")
    }
    val moduleTypeLabel = module.modulKategorie.trim()
    val stateLabel = editorStateLabel(module)
    val stateColor = editorStateColor(module)
    val metaFontSize = when {
        showStateMeta -> 11.px
        showInstalledMeta -> 10.px
        else -> 12.px
    }
    val metaGap = when {
        showStateMeta -> 8.px
        showInstalledMeta -> 6.px
        else -> 12.px
    }
    val visibleTypeLabel = when {
        showStateMeta -> moduleTypeLabel.take(12)
        showInstalledMeta -> moduleTypeLabel.take(10)
        else -> moduleTypeLabel.take(18)
    }
    Div({
        style {
            width(360.px)
            height(405.px)
            padding(12.px)
            borderRadius(20.px)
            overflow("hidden")
            property("border", "1px solid $borderColor")
            property(
                "box-shadow",
                when {
                    selected -> "0 20px 42px rgba(0,0,0,0.28)"
                    hovered -> "0 18px 36px rgba(0,0,0,0.22)"
                    else -> "0 14px 28px rgba(0,0,0,0.18)"
                }
            )
            property(
                "background",
                if (hovered) {
                    "linear-gradient(180deg, rgba(60, 66, 76, 0.96) 0%, rgba(34, 38, 45, 0.98) 100%)"
                } else {
                    DashboardTheme.PREMIUM_SURFACE_BG
                }
            )
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            boxSizing("border-box")
            cursor("pointer")
            property("transition", "background-color 140ms ease, box-shadow 140ms ease")
        }
        onClick { event ->
            val multi = (event.nativeEvent.asDynamic().ctrlKey as? Boolean == true) ||
                (event.nativeEvent.asDynamic().metaKey as? Boolean == true)
            onSelect(multi)
        }
        onMouseEnter { hovered = true }
        onMouseLeave { hovered = false }
    }) {
        Div({
            style {
                width(100.percent)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                minHeight(34.px)
                paddingTop(2.px)
            }
        }) {
            Span({
                style {
                    color(Color("#f5f6f8"))
                    fontSize(16.px)
                    fontWeight("700")
                    lineHeight("1.15")
                    textAlign("center")
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("word-break", "break-word")
                }
            }) { Text(module.title) }
        }

        Div({
            style {
                width(100.percent)
                height(204.px)
                position(Position.Relative)
                flexShrink(0)
                marginTop(4.px)
                borderRadius(16.px)
                overflow("hidden")
                property(
                    "background",
                    if (module.iconPath.isNotBlank()) {
                        "linear-gradient(115deg, rgba(91,155,255,0.10) 0%, rgba(15,19,26,0.42) 68%), url('${module.iconPath}') center/cover no-repeat"
                    } else {
                        UISettings.BLUE_SURFACE_GRADIENT
                    }
                )
            }
        }) {
            if (module.iconPath.isBlank()) {
                Span({
                    style {
                        position(Position.Absolute)
                        left(50.percent)
                        top(50.percent)
                        property("transform", "translate(-50%, -50%)")
                        color(Color("#eef2f9"))
                        fontSize(14.px)
                        fontWeight("700")
                        backgroundColor(Color("rgba(0,0,0,0.30)"))
                        padding(8.px, 12.px)
                        borderRadius(10.px)
                    }
                }) { Text("Kein Bild") }
            }
        }

        Div({
            style {
                width(100.percent)
                flexGrow(1)
                minHeight(0.px)
                padding(4.px, 12.px, 10.px, 12.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.SpaceBetween)
                gap(6.px)
                boxSizing("border-box")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(if (showStateMeta) JustifyContent.FlexStart else JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    gap(metaGap)
                    flexWrap(if (showStateMeta) FlexWrap.Nowrap else FlexWrap.Wrap)
                    paddingBottom(10.px)
                    overflow("hidden")
                }
            }) {
                Span({ style { color(Color("#7f8ba0")); fontSize(metaFontSize); whiteSpace("nowrap") } }) {
                    Text("Version ${module.version}")
                }
                Span({ style { color(Color("#596273")); fontSize(metaFontSize); whiteSpace("nowrap") } }) { Text("|") }
                Span({
                    style {
                        color(Color("#7f8ba0"))
                        fontSize(metaFontSize)
                        whiteSpace("nowrap")
                        overflow("hidden")
                        property("text-overflow", "ellipsis")
                    }
                }) {
                    Text(visibleTypeLabel)
                }
                Span({ style { color(Color("#596273")); fontSize(metaFontSize); whiteSpace("nowrap") } }) { Text("|") }
                Span({ style { color(Color("#7f8ba0")); fontSize(metaFontSize); whiteSpace("nowrap") } }) {
                    Text("Screens: ${module.enabledScreens.size}")
                }
                if (showStateMeta) {
                    Span({ style { color(Color("#596273")); fontSize(metaFontSize); whiteSpace("nowrap") } }) { Text("|") }
                    Span({ style { color(stateColor); fontSize(metaFontSize); fontWeight("600"); whiteSpace("nowrap") } }) {
                        Text(stateLabel)
                    }
                } else if (showInstalledMeta && state == ModuleStateVal.INSTALLED) {
                    Span({ style { color(Color("#596273")); fontSize(metaFontSize); whiteSpace("nowrap") } }) { Text("|") }
                    Span({ style { color(Color("#66BB6A")); fontSize(metaFontSize); fontWeight("600"); whiteSpace("nowrap") } }) {
                        Text("Installiert")
                    }
                }
            }

            Div({
                style {
                    flexGrow(1)
                    minHeight(0.px)
                    overflow("hidden")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                }
            }) {
                Span({
                    style {
                        color(Color("#aeb7c6"))
                        fontSize(12.px)
                        lineHeight("1.45")
                        display(DisplayStyle.Block)
                        width(100.percent)
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "3")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("word-break", "break-word")
                        property("white-space", "normal")
                    }
                }) {
                    Text(module.description)
                }
            }
        }
    }
}






